import React from 'react';
import {
  TouchAppOutlined, CropSquareOutlined,
  RadioButtonCheckedOutlined, LinkOutlined, ChatBubbleOutlineOutlined,
  OndemandVideoOutlined, ForumOutlined, GridViewOutlined, TextFieldsOutlined, ImageOutlined,
  DesktopWindowsOutlined, TabletOutlined, StayCurrentLandscapeOutlined, PhoneIphoneOutlined,
} from '@mui/icons-material';

export const PRIMARY   = '#1075BA';
export const CANVAS_W  = 1440;
export const CANVAS_H  = 810;
export const ASPECT    = CANVAS_H / CANVAS_W; // 0.5625 — 16:9

export const SHAPE_TYPES = {
  polygon: {
    label: 'Hotspot',
    sub:   'Custom polygon shape',
    icon:  <TouchAppOutlined sx={{ fontSize: 18 }} />,
    color: '#1075BA',
  },
  rect: {
    label: 'Rectangle',
    sub:   'Rectangular click area',
    icon:  <CropSquareOutlined sx={{ fontSize: 18 }} />,
    color: '#8b4fcc',
  },
  circle: {
    label: 'Circle',
    sub:   'Circular hotspot',
    icon:  <RadioButtonCheckedOutlined sx={{ fontSize: 18 }} />,
    color: '#00a882',
  },
  link: {
    label: 'Link Area',
    sub:   'Navigates to a page',
    icon:  <LinkOutlined sx={{ fontSize: 18 }} />,
    color: '#e67e22',
  },
  chat: {
    label: 'Chat',
    sub:   'Opens a live chat panel',
    icon:  <ChatBubbleOutlineOutlined sx={{ fontSize: 18 }} />,
    color: '#0a7aff',
  },
  stream: {
    label: 'Livestream Player',
    sub:   'Embedded video player',
    icon:  <OndemandVideoOutlined sx={{ fontSize: 18 }} />,
    color: '#EF4444',
  },
  'livestream-chat': {
    label: 'Livestream Chat',
    sub:   'Full-height live chat panel',
    icon:  <ForumOutlined sx={{ fontSize: 18 }} />,
    color: '#0a7aff',
  },
  'exhibitor-grid': {
    label: 'Exhibitor Grid',
    sub:   'Grid of exhibitor booths',
    icon:  <GridViewOutlined sx={{ fontSize: 18 }} />,
    color: '#0d7a5f',
  },
  text: {
    label: 'Text',
    sub:   'Text overlay on the canvas',
    icon:  <TextFieldsOutlined sx={{ fontSize: 18 }} />,
    color: '#7c3aed',
  },
  image: {
    label: 'Image',
    sub:   'Image overlay on the canvas',
    icon:  <ImageOutlined sx={{ fontSize: 18 }} />,
    color: '#e67e22',
  },
};

export const CHAT_COLOR = '#0a7aff';

export function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export function pct(px, total) {
  return (px / total) * 100;
}

export function slugify(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'lobby';
}

// ── Breakpoints for responsive lobby builder ──────────────────────────────────
//
// key              canvasW   canvasH   stored under
// ─────────────────────────────────────────────────
// desktop          1440      810       (base — no override needed)
// tablet-landscape 1024      768       hotspot.responsive['tablet-landscape']
// tablet-portrait  768       1024      hotspot.responsive['tablet-portrait']
// mobile           375       812       hotspot.responsive['mobile']
//
// Coordinates are always stored as percentages (0–100) of the DESKTOP canvas
// (1440×810). The builder scales the canvas visually for each breakpoint but the
// SVG viewBox stays 1440×810 at all times so hotspot % coords mean the same
// thing across breakpoints. Override geometry is also % of 1440×810.
//
export const LOBBY_BREAKPOINTS = [
  {
    key:     'desktop',
    icon:    <DesktopWindowsOutlined sx={{ fontSize: 18 }} />,
    label:   'Desktop',
    short:   'Desktop',
    canvasW: 1440,
    canvasH: 810,
    color:   PRIMARY,
    group:   'desktop',
  },
  {
    key:     'tablet-landscape',
    icon:    <StayCurrentLandscapeOutlined sx={{ fontSize: 18 }} />,
    label:   'Tablet Landscape',
    short:   '1024×768',
    canvasW: 1024,
    canvasH: 768,
    color:   PRIMARY,
    group:   'tablet',
  },
  {
    key:     'tablet-portrait',
    icon:    <TabletOutlined sx={{ fontSize: 18 }} />,
    label:   'Tablet Portrait',
    short:   '768×1024',
    canvasW: 768,
    canvasH: 1024,
    color:   PRIMARY,
    group:   'tablet',
  },
  {
    key:     'mobile',
    icon:    <PhoneIphoneOutlined sx={{ fontSize: 18 }} />,
    label:   'Mobile',
    short:   '375×812',
    canvasW: 375,
    canvasH: 700,
    color:   PRIMARY,
    group:   'mobile',
  },
];

// All non-desktop bp keys in order — used for serialization / fallback chains
export const RESPONSIVE_BP_KEYS = ['tablet-landscape', 'tablet-portrait', 'mobile'];

/**
 * Resolve the effective hotspot geometry for a given breakpoint.
 * Coordinates are always % of the DESKTOP canvas (1440×810) regardless of bp.
 * Falls back to desktop values when no override exists for that bp.
 */
export function resolveHotspotForBp(hotspot, bp) {
  if (!bp || bp === 'desktop') return hotspot;
  const override = hotspot?.responsive?.[bp];
  if (!override) return hotspot;

  // Merge geometry — treat empty points array as "not overridden" and fall back to desktop
  const resolved = {
    ...hotspot,
    points: (override.points && override.points.length > 0) ? override.points : hotspot.points,
    meta:   { ...(hotspot.meta || {}), ...(override.meta || {}) },
  };

  // Merge bp-level style fields onto the top-level properties that renderers read
  const style = override.style;
  if (style) {
    if (style.hoverFillColor    !== undefined) resolved.hoverFillColor    = style.hoverFillColor;
    if (style.hoverFillOpacity  !== undefined) resolved.hoverFillOpacity  = style.hoverFillOpacity;
    if (style.hoverBorderColor  !== undefined) resolved.hoverBorderColor  = style.hoverBorderColor;
    if (style.hoverBorderOpacity !== undefined) resolved.hoverBorderOpacity = style.hoverBorderOpacity;
    if (style.strokeWidth       !== undefined) resolved.strokeWidth       = style.strokeWidth;
  }

  // Merge bp-level transition (only when explicitly set — undefined means "no override")
  if ('transitionVideoUrl' in override) {
    resolved.hotspotAction = {
      ...(hotspot.hotspotAction || {}),
      transitionVideoUrl: override.transitionVideoUrl,
    };
  }

  return resolved;
}

/**
 * Apply a responsive override for a specific breakpoint onto a hotspot.
 * patch = { points?, meta? }
 */
export function setHotspotBpOverride(hotspot, bp, patch) {
  if (!bp || bp === 'desktop') return hotspot;
  const existing = hotspot?.responsive?.[bp] || {};
  const merged = { ...existing, ...patch };
  return {
    ...hotspot,
    responsive: {
      ...(hotspot.responsive || {}),
      [bp]: merged,
    },
  };
}

/**
 * Returns true if the hotspot should be visible on the given breakpoint.
 * Defaults to visible (true) when no visibleOn map is set.
 */
export function isHotspotVisibleOnBp(hotspot, bp) {
  if (!hotspot?.visibleOn) return true;
  const key = bp || 'desktop';
  return hotspot.visibleOn[key] !== false;
}

// ── Per-breakpoint style overrides ───────────────────────────────────────────
//
// Style fields (fill, border, strokeWidth) are stored under:
//   hotspot.responsive[bp].style = { hoverFillColor, hoverFillOpacity,
//                                    hoverBorderColor, hoverBorderOpacity,
//                                    strokeWidth }
//
// On desktop, these live directly on the hotspot (existing schema).
// On non-desktop, we merge the bp style on top; if a field is missing in the
// bp style we fall back to the desktop value — the consumer never gets undefined.
//
// Transition video is stored under:
//   hotspot.responsive[bp].transitionVideoUrl
// With the rule: if no value is set for a bp, use null (not the desktop one),
// because a missing transition is intentional — the designer chose none.

/**
 * Resolve the effective style fields for a given breakpoint.
 * Falls back to desktop values for any field not explicitly set in the override.
 */
export function resolveHotspotStyleForBp(hotspot, bp) {
  const base = {
    hoverFillColor:    hotspot?.hoverFillColor    ?? '#1075BA',
    hoverFillOpacity:  hotspot?.hoverFillOpacity  ?? 0.2,
    hoverBorderColor:  hotspot?.hoverBorderColor  ?? '#1075BA',
    hoverBorderOpacity: hotspot?.hoverBorderOpacity ?? 0.85,
    strokeWidth:       hotspot?.strokeWidth        ?? 1.5,
  };
  if (!bp || bp === 'desktop') return base;
  const bpStyle = hotspot?.responsive?.[bp]?.style || {};
  return {
    hoverFillColor:    bpStyle.hoverFillColor    ?? base.hoverFillColor,
    hoverFillOpacity:  bpStyle.hoverFillOpacity  ?? base.hoverFillOpacity,
    hoverBorderColor:  bpStyle.hoverBorderColor  ?? base.hoverBorderColor,
    hoverBorderOpacity: bpStyle.hoverBorderOpacity ?? base.hoverBorderOpacity,
    strokeWidth:       bpStyle.strokeWidth        ?? base.strokeWidth,
  };
}

/**
 * Returns true if the hotspot has any explicit style override for the given bp.
 */
export function hasHotspotStyleBpOverride(hotspot, bp) {
  if (!bp || bp === 'desktop') return false;
  const bpStyle = hotspot?.responsive?.[bp]?.style;
  return !!bpStyle && Object.keys(bpStyle).length > 0;
}

/**
 * Returns true if the hotspot has an explicit transition override for the given bp.
 * (Even a null value counts as an override — the designer deliberately cleared it.)
 */
export function hasHotspotTransitionBpOverride(hotspot, bp) {
  if (!bp || bp === 'desktop') return false;
  const bpData = hotspot?.responsive?.[bp];
  return !!bpData && 'transitionVideoUrl' in bpData;
}

/**
 * Apply a style override patch for a specific breakpoint.
 * patch = subset of { hoverFillColor, hoverFillOpacity, hoverBorderColor,
 *                     hoverBorderOpacity, strokeWidth }
 */
export function setHotspotStyleBpOverride(hotspot, bp, patch) {
  if (!bp || bp === 'desktop') return hotspot;
  const existing    = hotspot?.responsive?.[bp] || {};
  const existStyle  = existing.style || {};
  return {
    ...hotspot,
    responsive: {
      ...(hotspot.responsive || {}),
      [bp]: { ...existing, style: { ...existStyle, ...patch } },
    },
  };
}

/**
 * Apply a transition video override for a specific breakpoint.
 * Pass null to explicitly clear the transition for this bp.
 */
export function setHotspotTransitionBpOverride(hotspot, bp, transitionVideoUrl) {
  if (!bp || bp === 'desktop') return hotspot;
  const existing = hotspot?.responsive?.[bp] || {};
  return {
    ...hotspot,
    responsive: {
      ...(hotspot.responsive || {}),
      [bp]: { ...existing, transitionVideoUrl },
    },
  };
}

/**
 * Get the effective transition video URL for a given breakpoint.
 * Unlike style, transition does NOT fall back to desktop — if not set for
 * this bp we return undefined so the caller knows there's no override.
 */
export function resolveHotspotTransitionForBp(hotspot, bp) {
  if (!bp || bp === 'desktop') return hotspot?.hotspotAction?.transitionVideoUrl ?? null;
  const bpData = hotspot?.responsive?.[bp];
  if (bpData && 'transitionVideoUrl' in bpData) return bpData.transitionVideoUrl;
  // No override set — return undefined to signal "no explicit selection"
  return undefined;
}

/**
 * Remove all style AND transition overrides for a given breakpoint.
 */
export function clearHotspotStyleBpOverride(hotspot, bp) {
  if (!bp || bp === 'desktop') return hotspot;
  const newResponsive = { ...(hotspot.responsive || {}) };
  if (newResponsive[bp]) {
    const { style, transitionVideoUrl, ...rest } = newResponsive[bp]; // eslint-disable-line no-unused-vars
    if (Object.keys(rest).length === 0) {
      delete newResponsive[bp];
    } else {
      newResponsive[bp] = rest;
    }
  }
  return { ...hotspot, responsive: newResponsive };
}